import React, { Suspense, useMemo } from 'react';
import Media from 'react-media';
import { useFeatureFlag } from '@trello/feature-flag-client';
import { useLazyComponent } from '@trello/use-lazy-component';
import { memberId } from '@trello/session-cookie';
import { useSharedState } from '@trello/shared-state';
import { Analytics } from '@trello/atlassian-analytics';
import { ChunkLoadErrorBoundary } from 'app/src/components/ChunkLoadErrorBoundary';
import { ErrorBoundary } from 'app/src/components/ErrorBoundary';
import { headerState } from 'app/src/components/Header/headerState';
import { HeaderSearchButton } from 'app/src/components/Header/HeaderSearchButton';
import { SearchInputSkeleton } from 'app/src/components/HeaderSkeleton';
import { ScreenBreakpoints } from 'app/src/components/Responsive';
import { SearchForm, SearchFormProps } from 'app/src/components/SearchForm';
import { CrossProductSearchInputSkeleton } from './CrossProductSearchInputSkeleton';
import { useCrossProductSearchUserDetailsQuery } from './CrossProductSearchUserDetailsQuery.generated';
import { useSearchTheme } from './useSearchTheme';

/**
 * The Cross Product Search component has an aggressive takeover below this
 * screen size, and requires at least this width for the best user experience.
 */
const FULL_PAGE_TAKEOVER_MEDIA_QUERY = 'only screen and (max-width: 790px)';

/**
 * Entry point for the Cross Product Search (XPS) component tree.
 * When the XPS feature flag is active and the member is Aa governed,
 * this wrapper will lazy load the full XPS component.
 */
export const CrossProductSearchWrapper: React.FC<
  Omit<SearchFormProps, 'renderTraceId'>
> = ({ focusRefOnDismiss }) => {
  const isXPSActive = useFeatureFlag('workflowers.cross-product-search', false);
  const [{ brandingName }] = useSharedState(headerState);

  const { data, loading } = useCrossProductSearchUserDetailsQuery({
    variables: { memberId: memberId || 'me' },
  });

  const theme = useSearchTheme();

  // This event will fire twice in dev env due to Strict Mode.
  const renderTraceId = useMemo(() => {
    return Analytics.startTask({
      taskName: 'view-search/form',
      source: 'searchInlineDialog',
    });
  }, []);

  const CrossProductSearch = useLazyComponent(
    () =>
      import(
        /* webpackChunkName: "cross-product-search" */ './CrossProductSearch'
      ),
    { namedImport: 'CrossProductSearch', preload: isXPSActive },
  );

  const renderSearch = () => {
    if (loading) {
      // Once XPS has been fully rolled out, use the XPS Input Skeleton here.
      return <SearchInputSkeleton />;
    }

    if (!isXPSActive || !data?.member?.isAaMastered) {
      return (
        <SearchForm
          focusRefOnDismiss={focusRefOnDismiss}
          renderTraceId={renderTraceId}
        />
      );
    }

    return (
      <ErrorBoundary
        tags={{
          ownershipArea: 'trello-workflowers',
          feature: 'Cross Product Search',
        }}
      >
        <ChunkLoadErrorBoundary fallback={null}>
          <Suspense
            fallback={<CrossProductSearchInputSkeleton theme={theme} />}
          >
            <CrossProductSearch theme={theme} />
          </Suspense>
        </ChunkLoadErrorBoundary>
      </ErrorBoundary>
    );
  };

  return (
    <Media
      queries={{
        small: FULL_PAGE_TAKEOVER_MEDIA_QUERY,
        medium: ScreenBreakpoints.Medium,
      }}
    >
      {(matches: { small: boolean; medium: boolean }) =>
        matches.small || (matches.medium && brandingName) ? (
          <HeaderSearchButton />
        ) : (
          renderSearch()
        )
      }
    </Media>
  );
};
